// src/components/Thr.jsx
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function Thr({ api }) {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tanggal acuan perhitungan masa keanggotaan (default: hari ini)
  const [refDate, setRefDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/thr", { params: { ref_date: refDate } });
      setData(res.data.data || []);
      setSummary({
        ref_date: res.data.ref_date,
        count: res.data.count,
        count_with_date: res.data.count_with_date,
        total_thr_formatted: res.data.total_thr_formatted,
      });
      setCurrentPage(1);
    } catch (e) {
      console.error("Error fetching THR:", e);
      setError("Gagal memuat data THR. Pastikan backend berjalan & token valid.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ─── Export Excel ──────────────────────────────────────────────────────────
  const exportExcel = () => {
    if (!data.length) return;

    const rows = data.map((t, i) => ({
      No: i + 1,
      "No Anggota": t.member_no,
      Nama: t.name,
      "Tgl Masuk": t.join_date || "-",
      "Masa Keanggotaan (thn)": t.masa_tahun ?? "-",
      "Kategori": t.bracket,
      "THR": t.thr,
      "Keterangan": t.note || "",
    }));

    // Baris total di bawah
    rows.push({
      No: "",
      "No Anggota": "",
      Nama: "TOTAL",
      "Tgl Masuk": "",
      "Masa Keanggotaan (thn)": "",
      "Kategori": "",
      "THR": data.reduce((sum, t) => sum + (t.thr || 0), 0),
      "Keterangan": "",
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "THR 2026");
    XLSX.writeFile(wb, `THR_Koperasi_${refDate}.xlsx`);
  };

  // ─── Export PDF ────────────────────────────────────────────────────────────
  const exportPDF = () => {
    if (!data.length) return;

    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.text("Daftar Uang THR Koperasi", 14, 15);
    doc.setFontSize(10);
    doc.text(`Tanggal acuan: ${formatDate(refDate)}`, 14, 22);
    doc.text(
      `Dicetak: ${new Date().toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}`,
      14,
      28
    );

    const tableData = data.map((t, i) => [
      i + 1,
      t.member_no,
      t.name,
      t.join_date || "-",
      t.masa_tahun ?? "-",
      t.bracket,
      t.thr_formatted,
    ]);

    autoTable(doc, {
      head: [["No", "No Anggota", "Nama", "Tgl Masuk", "Masa (thn)", "Kategori", "THR"]],
      body: tableData,
      startY: 34,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 130, 246] },
      foot: [[
        { content: "TOTAL", colSpan: 6, styles: { halign: "right", fontStyle: "bold" } },
        { content: summary?.total_thr_formatted || "-", styles: { fontStyle: "bold" } },
      ]],
    });

    doc.save(`THR_Koperasi_${refDate}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-bold text-slate-800">Uang THR Koperasi</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Tanggal acuan</label>
          <input
            type="date"
            value={refDate}
            onChange={(e) => setRefDate(e.target.value)}
            className="border rounded-xl px-3 py-2 text-sm"
          />
          <button
            onClick={fetchData}
            className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700"
          >
            Hitung
          </button>
        </div>
      </div>

      {/* Ringkasan */}
      {summary && (
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl shadow-sm p-5 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <p className="text-sm opacity-90">Total Anggota</p>
            <p className="text-2xl font-bold mt-1">{summary.count}</p>
            <p className="text-xs opacity-80 mt-1">Tanggal acuan {formatDate(summary.ref_date)}</p>
          </div>
          <div className="rounded-2xl shadow-sm p-5 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <p className="text-sm opacity-90">Total THR</p>
            <p className="text-2xl font-bold mt-1">{summary.total_thr_formatted}</p>
            <p className="text-xs opacity-80 mt-1">{summary.count_with_date} anggota dihitung</p>
          </div>
          <div className="rounded-2xl shadow-sm p-5 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <p className="text-sm opacity-90">Belum ada Tgl Masuk</p>
            <p className="text-2xl font-bold mt-1">{summary.count - summary.count_with_date}</p>
            <p className="text-xs opacity-80 mt-1">Perlu diisi manual</p>
          </div>
        </div>
      )}

      {/* Tombol Export */}
      <div className="flex gap-3">
        <button
          onClick={exportExcel}
          disabled={!data.length}
          className="px-3 py-2 rounded-xl bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
        >
          Export Excel
        </button>
        <button
          onClick={exportPDF}
          disabled={!data.length}
          className="px-3 py-2 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
        >
          Export PDF
        </button>
      </div>

      {error && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Tabel */}
      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3">No</th>
              <th className="text-left px-4 py-3">No Anggota</th>
              <th className="text-left px-4 py-3">Nama</th>
              <th className="text-left px-4 py-3">Tgl Masuk</th>
              <th className="text-left px-4 py-3">Masa (thn)</th>
              <th className="text-left px-4 py-3">Kategori</th>
              <th className="text-left px-4 py-3">THR</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center py-6 text-slate-400">
                  Memuat data...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-6 text-slate-400">
                  Tidak ada data
                </td>
              </tr>
            ) : (
              paginatedData.map((t, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">
                    {(currentPage - 1) * itemsPerPage + i + 1}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.member_no}</td>
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(t.join_date)}</td>
                  <td className="px-4 py-3 text-slate-600">{t.masa_tahun ?? "-"}</td>
                  <td className="px-4 py-3">
                    {t.note ? (
                      <span className="text-xs px-2 py-1 rounded-lg bg-orange-100 text-orange-700">
                        {t.note}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-700">
                        {t.bracket}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    {t.thr_formatted}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && data.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 text-sm border-t bg-slate-50">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-slate-600">
              Page <b>{currentPage}</b> dari <b>{totalPages}</b>
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
